import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './Reels.module.css';
import { 
  IoHeart, 
  IoHeartOutline, 
  IoArrowRedoOutline, 
  IoTrashOutline, 
  IoMenu, 
  IoExpand, 
  IoRefresh,
  IoPlayCircle,
  IoCloudOfflineOutline,
  IoCheckmarkCircle,
  IoHomeOutline,
  IoGameControllerOutline,
  IoMusicalNotesOutline,
  IoSchoolOutline,
  IoCalendarOutline,
  IoRestaurantOutline
} from 'react-icons/io5';
import { MdMessage, MdComment } from 'react-icons/md';
import { Colors, Keys } from '../../../helpers/constants';
import { getUserId, getUnreadMessagesCount } from '../../../helpers/userHelpers';
import { uppercaseFirstLetter, formatCount, replaceLocalhostInUrl } from '../../../helpers/formatHelpers';
import { GET, POST, deleteRequest } from '../../../config/api';
import { showToast } from '../../../helpers/toast';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../config/config';
import CommentsModal from '../../../components/CommentsModal';
import { useDrawer } from '../../../contexts/DrawerContext';
import { Drawer, useMediaQuery, useTheme } from '@mui/material';
import CustomDrawer from '../../../components/navigation/CustomDrawer';
import CoreTabNavigator from '../../../components/navigation/CoreTabNavigator';

const MediaItem = ({ mediaItem, isActive, isVisible }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const videoRef = useRef(null);
  const maxRetries = 3;

  useEffect(() => {
    if (isActive && isVisible && mediaItem.type === 'video') {
      setVideoError(false);
      setRetryCount(0);
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, [isActive, isVisible, mediaItem.type]);

  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying && !videoError) {
        videoRef.current.play().catch(() => {
          // Auto-play was prevented
        });
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying, videoError]);

  const handleVideoPress = () => {
    if (mediaItem.type === 'video') {
      if (videoError) {
        setVideoError(false);
        setRetryCount(0);
      } else {
        setIsPlaying(!isPlaying);
      }
    }
  };

  const handleVideoError = () => {
    if (retryCount < maxRetries) {
      setRetryCount((prev) => prev + 1);
      setTimeout(() => {
        setVideoError(false);
      }, 1000);
    } else {
      setVideoError(true);
    }
  };

  return (
    <div className={styles.mediaContainer} onClick={handleVideoPress}>
      {mediaItem.type === 'image' ? (
        <img
          src={replaceLocalhostInUrl(mediaItem.url)}
          alt="Reel media"
          className={styles.backgroundMedia}
          onError={(e) => {
            e.target.src = '/placeholder-image.png';
          }}
        />
      ) : (
        <div className={styles.videoContainer}>
          {!videoError ? (
            <video
              ref={videoRef}
              src={replaceLocalhostInUrl(mediaItem.url)}
              className={styles.backgroundMedia}
              loop
              muted={false}
              playsInline
              onLoadedData={() => {
                setVideoError(false);
              }}
              onError={handleVideoError}
            />
          ) : (
            <div className={styles.errorContainer}>
              <IoCloudOfflineOutline size={48} color={Colors.DANGER} />
              <p className={styles.errorText}>Network Error</p>
              <p className={styles.retryText}>Tap to retry</p>
            </div>
          )}

          {!isPlaying && !videoError && (
            <div className={styles.playIndicator}>
              <div className={styles.pauseButton}>
                <IoPlayCircle size={80} color={Colors.WHITE} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ReelItem = ({ reel, isActive, userId, onOpenComments, onDeleteReel, onOpenDrawer, onRefresh }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isLiked, setIsLiked] = useState(reel.likes.includes(userId));
  const [likesCount, setLikesCount] = useState(reel.likes.length);
  const [showHeart, setShowHeart] = useState(false);
  const unreadMessages = getUnreadMessagesCount();
  const navigate = useNavigate();
  const mediaPagerRef = useRef(null);

  const handleLike = async () => {
    const newLikedState = !isLiked;
    const previousLikedState = isLiked;
    const previousLikesCount = likesCount;

    // Optimistic update
    setIsLiked(newLikedState);
    setLikesCount((prev) => (newLikedState ? prev + 1 : prev - 1));

    // Show floating heart animation
    if (newLikedState) {
      setShowHeart(true);
      setTimeout(() => setShowHeart(false), 1000);
    }

    try {
      const { data, error } = await POST(`/reels/${reel._id}/like`, {});
      if (error) {
        // Rollback on error
        setIsLiked(previousLikedState);
        setLikesCount(previousLikesCount);
        showToast('Failed to update like', '', 'error', { position: 'top-center' });
      } else {
        // Update with server response
        setIsLiked(data.isLiked);
        setLikesCount(data.likesCount);
      }
    } catch (error) {
      // Rollback on error
      setIsLiked(previousLikedState);
      setLikesCount(previousLikesCount);
      showToast('Failed to update like', '', 'error', { position: 'top-center' });
    }
  };

  const handleComment = () => {
    onOpenComments(reel);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: reel.title || 'Check out this reel!',
        text: reel.description || '',
        url: window.location.origin + `/reel/${reel._id}`,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.origin + `/reel/${reel._id}`);
      showToast('Link copied to clipboard', '', 'success', { position: 'top-center' });
    }
  };

  const handleOpenReel = () => {
    navigate(`/reel/${reel._id}`);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this reel? This action cannot be undone.')) {
      deleteReel();
    }
  };

  const deleteReel = async () => {
    const { error } = await deleteRequest({ url: `/reels/${reel._id}` });
    if (error) {
      showToast('Failed to delete reel', '', 'error', { position: 'top-center' });
    } else {
      showToast('Reel deleted successfully', '', 'success', { position: 'top-center' });
      onDeleteReel(reel._id);
    }
  };

  return (
    <div className={styles.reelContainer}>
      {/* Media Horizontal Pager */}
      {reel.media.length > 1 ? (
        <div className={styles.mediaPager} ref={mediaPagerRef}>
          {reel.media.map((mediaItem, index) => (
            <div key={index} className={styles.mediaPage}>
              <MediaItem 
                mediaItem={mediaItem} 
                isActive={isActive && index === currentMediaIndex} 
                isVisible={index === currentMediaIndex} 
              />
            </div>
          ))}
        </div>
      ) : (
        <MediaItem mediaItem={reel.media[0]} isActive={isActive} isVisible={true} />
      )}

      {/* Menu Drawer Button */}
      <div className={styles.modernDrawerButton} onClick={onOpenDrawer}>
        <div className={styles.modernDrawerIconContainer}>
          <IoMenu size={32} color={Colors.WHITE} />
        </div>
      </div>

      {/* Refresh Button */}
      <div className={styles.modernRefreshButton} onClick={onRefresh}>
        <div className={styles.modernRefreshIconContainer}>
          <IoRefresh size={32} color={Colors.WHITE} />
        </div>
      </div>

      {/* Chats Button */}
      <div className={styles.modernChatButton} onClick={() => navigate(routes.chats)}>
        <div className={styles.modernChatIconContainer}>
          <MdMessage size={24} color={Colors.WHITE} />
          {unreadMessages > 0 && <div className={styles.modernMessageBadge} />}
        </div>
      </div>

      {/* Open Individual Reel Button */}
      <div className={styles.modernExpandButton} onClick={handleOpenReel}>
        <div className={styles.modernExpandIconContainer}>
          <IoExpand size={24} color={Colors.WHITE} />
        </div>
      </div>

      {/* Media Indicator Dots */}
      {reel.media.length > 1 && (
        <div className={styles.mediaIndicator}>
          {reel.media.map((_, index) => (
            <div 
              key={index} 
              className={styles.mediaIndicatorDot}
              style={{ opacity: index === currentMediaIndex ? 1 : 0.4 }}
            />
          ))}
        </div>
      )}

      {/* Floating Heart Animation */}
      {showHeart && (
        <div className={`${styles.floatingHeart} ${showHeart ? styles.floatingHeartActive : ''}`}>
          <IoHeart size={100} color={Colors.DANGER} />
        </div>
      )}

      {/* Bottom Content */}
      <div className={styles.modernBottomContent}>
        {/* User Info, title and description */}
        <div className={styles.leftContent}>
          <div
            className={styles.modernUserInfo}
            onClick={() => {
              if (reel.user._id === userId) {
                navigate(routes.profile);
              } else {
                navigate(`/user/${reel.user._id}`);
              }
            }}
          >
            <div className={styles.modernAvatarContainer}>
              <img
                src={replaceLocalhostInUrl(reel.user.pictureURL || '/default-avatar.png')}
                alt={reel.user.username}
                className={styles.modernAvatar}
                onError={(e) => {
                  e.target.src = '/default-avatar.png';
                }}
              />
              <div className={styles.modernAvatarBorder} />
            </div>
            <div className={styles.modernUserDetails}>
              <span className={styles.modernUsername}>@{uppercaseFirstLetter(reel.user.username)}</span>
              <div className={styles.verifiedBadge}>
                <IoCheckmarkCircle size={14} color="#1DA1F2" />
              </div>
            </div>
          </div>
          {reel.title && <h3 className={styles.modernTitle}>{reel.title}</h3>}
          {reel.description && <p className={styles.modernDescription}>{reel.description}</p>}

          {/* Display the products links linked to the reel */}
          {(reel.apartments?.length > 0 || 
            reel.universities?.length > 0 || 
            reel.events?.length > 0 || 
            reel.restaurants?.length > 0 || 
            reel.nightLifeTables?.length > 0 || 
            reel.gameTournaments?.length > 0) && (
            <div className={styles.linkedContentContainer}>
              <p className={styles.linkedContentHeader}>Discover</p>
              <div className={styles.linkScrollView}>
                {reel.gameTournaments?.map((item, index) => (
                  <div 
                    key={index} 
                    className={styles.modernLinkCard} 
                    onClick={() => navigate(`/match/${item._id}`)}
                  >
                    <div className={styles.modernLinkImageContainer}>
                      <img 
                        src={replaceLocalhostInUrl(item.game?.image || '/placeholder.png')} 
                        alt="Game"
                        className={styles.modernLinkImage}
                        onError={(e) => { e.target.src = '/placeholder.png'; }}
                      />
                      <div className={styles.modernLinkOverlay}>
                        <IoGameControllerOutline size={16} color={Colors.WHITE} />
                      </div>
                    </div>
                    <div className={styles.modernLinkTextContainer}>
                      <p className={styles.modernLinkLabel}>Gaming</p>
                      <p className={styles.modernLinkSubtext}>{item.game?.title || 'View Details'}</p>
                    </div>
                  </div>
                ))}
                {reel.nightLifeTables?.map((item, index) => (
                  <div 
                    key={index} 
                    className={styles.modernLinkCard} 
                    onClick={() => navigate(`/party-event/${item._id}`)}
                  >
                    <div className={styles.modernLinkImageContainer}>
                      <img 
                        src={item.placePhotos?.[0] ? `https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photoreference=${item.placePhotos[0]}&key=${Keys.MAPS_KEY}` : replaceLocalhostInUrl('/placeholder.png')}
                        alt="Nightlife"
                        className={styles.modernLinkImage}
                        onError={(e) => { e.target.src = '/placeholder.png'; }}
                      />
                      <div className={styles.modernLinkOverlay}>
                        <IoMusicalNotesOutline size={16} color={Colors.WHITE} />
                      </div>
                    </div>
                    <div className={styles.modernLinkTextContainer}>
                      <p className={styles.modernLinkLabel}>Nightlife</p>
                      <p className={styles.modernLinkSubtext}>{item.title || 'View Details'}</p>
                    </div>
                  </div>
                ))}
                {reel.apartments?.map((item, index) => (
                  <div 
                    key={index} 
                    className={styles.modernLinkCard} 
                    onClick={() => navigate(`/apartment/${item._id}`)}
                  >
                    <div className={styles.modernLinkImageContainer}>
                      <img 
                        src={replaceLocalhostInUrl(item.image || '/placeholder.png')}
                        alt="Apartment"
                        className={styles.modernLinkImage}
                        onError={(e) => { e.target.src = '/placeholder.png'; }}
                      />
                      <div className={styles.modernLinkOverlay}>
                        <IoHomeOutline size={16} color={Colors.WHITE} />
                      </div>
                    </div>
                    <div className={styles.modernLinkTextContainer}>
                      <p className={styles.modernLinkLabel}>Apartment</p>
                      <p className={styles.modernLinkSubtext}>{item.name || 'View Details'}</p>
                    </div>
                  </div>
                ))}
                {reel.universities?.map((item, index) => (
                  <div 
                    key={index} 
                    className={styles.modernLinkCard} 
                    onClick={() => navigate(routes.universitiesServices)}
                  >
                    <div className={styles.modernLinkImageContainer}>
                      <img 
                        src={replaceLocalhostInUrl(item.pictureURL || '/placeholder.png')}
                        alt="University"
                        className={styles.modernLinkImage}
                        onError={(e) => { e.target.src = '/placeholder.png'; }}
                      />
                      <div className={styles.modernLinkOverlay}>
                        <IoSchoolOutline size={16} color={Colors.WHITE} />
                      </div>
                    </div>
                    <div className={styles.modernLinkTextContainer}>
                      <p className={styles.modernLinkLabel}>University</p>
                      <p className={styles.modernLinkSubtext}>{item.name || 'View Details'}</p>
                    </div>
                  </div>
                ))}
                {reel.events?.map((item, index) => (
                  <div 
                    key={index} 
                    className={styles.modernLinkCard} 
                    onClick={() => navigate(`/event/${item._id}`)}
                  >
                    <div className={styles.modernLinkImageContainer}>
                      <img 
                        src={replaceLocalhostInUrl(item.pictureURL || '/placeholder.png')}
                        alt="Event"
                        className={styles.modernLinkImage}
                        onError={(e) => { e.target.src = '/placeholder.png'; }}
                      />
                      <div className={styles.modernLinkOverlay}>
                        <IoCalendarOutline size={16} color={Colors.WHITE} />
                      </div>
                    </div>
                    <div className={styles.modernLinkTextContainer}>
                      <p className={styles.modernLinkLabel}>Event</p>
                      <p className={styles.modernLinkSubtext}>{item.title || 'View Details'}</p>
                    </div>
                  </div>
                ))}
                {reel.restaurants?.map((item, index) => (
                  <div 
                    key={index} 
                    className={styles.modernLinkCard} 
                    onClick={() => navigate(`/restaurant/${item._id}`)}
                  >
                    <div className={styles.modernLinkImageContainer}>
                      <img 
                        src={replaceLocalhostInUrl(item.pictureURL || '/placeholder.png')}
                        alt="Restaurant"
                        className={styles.modernLinkImage}
                        onError={(e) => { e.target.src = '/placeholder.png'; }}
                      />
                      <div className={styles.modernLinkOverlay}>
                        <IoRestaurantOutline size={16} color={Colors.WHITE} />
                      </div>
                    </div>
                    <div className={styles.modernLinkTextContainer}>
                      <p className={styles.modernLinkLabel}>Restaurant</p>
                      <p className={styles.modernLinkSubtext}>{item.name || 'View Details'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Display the categories linked to the reel */}
          {reel.tags?.length > 0 && (
            <div className={styles.modernTagsContainer}>
              <div className={styles.modernTagsScrollView}>
                {reel.tags.map((tag, index) => (
                  <div key={index} className={styles.modernTagItem}>
                    <span className={styles.modernTagText}>#{tag}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className={styles.modernRightActions}>
          {/* Like Button */}
          <div className={styles.modernActionButton} onClick={handleLike}>
            <div className={`${styles.modernActionIconContainer} ${isLiked ? styles.likedActionContainer : ''}`}>
              {isLiked ? (
                <IoHeart size={32} color={Colors.WHITE} />
              ) : (
                <IoHeartOutline size={32} color={Colors.WHITE} />
              )}
            </div>
            <span className={styles.modernActionText}>{formatCount(likesCount)}</span>
          </div>

          {/* Comment Button */}
          <div className={styles.modernActionButton} onClick={handleComment}>
            <div className={styles.modernActionIconContainer}>
              <MdComment size={32} color={Colors.WHITE} />
            </div>
            <span className={styles.modernActionText}>{formatCount(reel.comments?.length || 0)}</span>
          </div>

          {/* Share Button */}
          <div className={styles.modernActionButton} onClick={handleShare}>
            <div className={styles.modernActionIconContainer}>
              <IoArrowRedoOutline size={32} color={Colors.WHITE} />
            </div>
            <span className={styles.modernActionText}>{formatCount(0)}</span>
          </div>

          {/* Delete Button - Only visible to owner */}
          {reel.user._id === userId && (
            <div className={styles.modernActionButton} onClick={handleDelete}>
              <div className={`${styles.modernActionIconContainer} ${styles.deleteActionContainer}`}>
                <IoTrashOutline size={32} color={Colors.WHITE} />
              </div>
              <span className={styles.modernActionText}>Delete</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DRAWER_WIDTH = 280;

const Reels = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCommentsModalVisible, setIsCommentsModalVisible] = useState(false);
  const [currentReelComments, setCurrentReelComments] = useState([]);
  const [currentReelId, setCurrentReelId] = useState('');
  const containerRef = useRef(null);
  const userId = getUserId();
  const navigate = useNavigate();
  const { isOpen, openDrawer, closeDrawer } = useDrawer();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchReels = async () => {
    setLoading(true);
    const { data, error } = await GET('/reels');
    if (error) {
      showToast("Couldn't fetch reels", '', 'error');
      navigate(routes.app);
    } else {
      setReels(data?.reels || []);
    }
    setLoading(false);
  };

  const handleOpenComments = async (reel) => {
    setCurrentReelId(reel._id);
    let comments = [];

    const { data, error } = await GET(`/reels/${reel._id}/comments`, {});
    if (error) {
      showToast("Couldn't fetch comments", '', 'error', { position: 'top-center' });
    } else {
      comments = data?.comments || [];
    }

    setCurrentReelComments(comments);
    setIsCommentsModalVisible(true);
  };

  const handleAddComment = (comment) => {
    setCurrentReelComments((prev) => [...prev, comment]);
    // Update the reel's comment count locally
    setReels((prev) =>
      prev.map((reel) =>
        reel._id === currentReelId ? { ...reel, comments: [...reel.comments, comment._id] } : reel
      )
    );
  };

  const handleCloseComments = () => {
    setIsCommentsModalVisible(false);
    setCurrentReelComments([]);
    setCurrentReelId('');
  };

  const handleDeleteReel = async (reelId) => {
    // Refresh the reels list from server to ensure consistency
    await fetchReels();
  };

  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const scrollTop = containerRef.current.scrollTop;
      const windowHeight = window.innerHeight;
      const newIndex = Math.round(scrollTop / windowHeight);
      
      if (newIndex !== currentIndex && newIndex >= 0 && newIndex < reels.length) {
        setCurrentIndex(newIndex);
      }
    }
  }, [currentIndex, reels.length]);

  useEffect(() => {
    fetchReels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleDrawerClose = () => {
    closeDrawer();
  };

  return (
    <CoreTabNavigator>
      {/* Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={isOpen}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
            zIndex: theme.zIndex.drawer,
          },
        }}
      >
        <CustomDrawer onClose={handleDrawerClose} />
      </Drawer>

      <div className={styles.container} ref={containerRef}>
        {loading ? (
        <div className={styles.loadingContainer}>
          <p className={styles.loadingText}>Loading reels...</p>
        </div>
      ) : reels.length > 0 ? (
        <>
          <div className={styles.pagerView}>
            {reels.map((reel, index) => (
              <div key={reel._id} className={styles.page}>
                <ReelItem
                  reel={reel}
                  isActive={index === currentIndex}
                  userId={userId}
                  onOpenComments={handleOpenComments}
                  onDeleteReel={handleDeleteReel}
                  onOpenDrawer={openDrawer}
                  onRefresh={fetchReels}
                />
              </div>
            ))}
          </div>

          {/* Page Indicator */}
          <div className={styles.pageIndicator}>
            {reels.map((_, index) => (
              <div
                key={index}
                className={styles.indicatorDot}
                style={{ opacity: index === currentIndex ? 1 : 0.4 }}
              />
            ))}
          </div>
        </>
      ) : (
        <div className={styles.loadingContainer}>
          <p className={styles.loadingText}>No reels available</p>
          <div className={styles.modernRefreshButton} onClick={fetchReels}>
            <div className={styles.modernRefreshIconContainer}>
              <IoRefresh size={32} color={Colors.WHITE} />
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      <CommentsModal
        visible={isCommentsModalVisible}
        onClose={handleCloseComments}
        reelId={currentReelId}
        comments={currentReelComments}
        onAddComment={handleAddComment}
      />
      </div>
    </CoreTabNavigator>
  );
};

export default Reels;

